import { CheckCircle2 } from 'lucide-react';

import { cn } from '@/lib/utils';

const STEPS = [
  { number: 1, label: 'Client Details', shortLabel: 'Client' },
  { number: 2, label: 'Risk Assessment Checks', shortLabel: 'Checks' },
  { number: 3, label: 'Due Diligence Level', shortLabel: 'Level' },
  { number: 4, label: 'Report', shortLabel: 'Report' },
] as const;

interface WizardStepperProps {
  currentStep: number;
  completedSteps: number[];
  variant?: 'vertical' | 'horizontal';
}

export function WizardStepper({
  currentStep,
  completedSteps,
  variant = 'vertical',
}: WizardStepperProps) {
  const isHorizontal = variant === 'horizontal';

  return (
    <div
      className={cn(
        isHorizontal && 'flex w-full items-center justify-between gap-1 sm:justify-center sm:gap-2',
      )}
    >
      {STEPS.map((step, index) => {
        const isCompleted = completedSteps.includes(step.number);
        const isCurrent = step.number === currentStep;
        const isLast = index === STEPS.length - 1;

        return (
          <div
            key={step.number}
            className={cn(
              'flex items-start gap-3',
              isHorizontal && 'flex-row items-center gap-1 sm:gap-2',
            )}
          >
            {/* Step indicator */}
            <div className={cn('flex flex-col items-center', isHorizontal && 'flex-row shrink-0')}>
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 text-[#051923]" />
              ) : (
                <div
                  className={cn(
                    'h-5 w-5 shrink-0 rounded-full border-2',
                    isCurrent ? 'border-[#131313] bg-[#131313]' : 'border-gray-300 bg-white',
                  )}
                />
              )}
              {/* Connecting line (dotted) - vertical or horizontal */}
              {!isLast &&
                (isHorizontal ? (
                  <div
                    className="ml-1 h-0.5 w-3 shrink-0 sm:w-4"
                    style={{
                      backgroundImage: `repeating-linear-gradient(to right, ${isCompleted ? '#051923' : '#9CA3AF'} 0px, ${isCompleted ? '#051923' : '#9CA3AF'} 4px, transparent 4px, transparent 8px)`,
                    }}
                  />
                ) : (
                  <div
                    className="mt-1 h-7 w-0.5"
                    style={{
                      backgroundImage: `repeating-linear-gradient(to bottom, ${isCompleted ? '#051923' : '#9CA3AF'} 0px, ${isCompleted ? '#051923' : '#9CA3AF'} 4px, transparent 4px, transparent 8px)`,
                    }}
                  />
                ))}
            </div>
            {/* Step label - only show current step in horizontal mode */}
            {isHorizontal ? (
              isCurrent && (
                <span className="truncate text-[13px] font-medium leading-5 text-[#051923] sm:text-[14px]">
                  <span className="hidden xs:inline">{step.label}</span>
                  <span className="xs:hidden">{step.shortLabel}</span>
                </span>
              )
            ) : (
              <span
                className={cn(
                  'text-[14px] leading-5',
                  isCurrent || isCompleted ? 'font-medium text-[#051923]' : 'text-gray-500',
                )}
              >
                {step.label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
